// Deliberately not a general CSS parser. Only validated, local presentation
// values are reconstructed; URLs, custom properties and layout controls never pass.
const COLORS = Object.freeze({
  white: '#ffffff',
  black: '#000000',
  red: '#ff0000',
  green: '#008000',
  blue: '#0000ff',
  yellow: '#ffff00',
  orange: '#ffa500',
  gray: '#808080',
  grey: '#808080',
  silver: '#c0c0c0',
  navy: '#000080',
  purple: '#800080',
  teal: '#008080',
  maroon: '#800000',
  lime: '#00ff00',
  aqua: '#00ffff',
  fuchsia: '#ff00ff',
  olive: '#808000',
  tomato: '#ff6347'
});
function color(value) {
  const v = value.trim().toLowerCase();
  if (Object.hasOwn(COLORS, v)) return COLORS[v];
  if (/^#[\da-f]{6}$/.test(v)) return v;
  if (/^#[\da-f]{3}$/.test(v))
    return '#' + [...v.slice(1)].map((x) => x + x).join('');
  const rgb = v.match(
    /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/
  );
  if (rgb && rgb.slice(1).every((x) => Number(x) <= 255))
    return (
      '#' +
      rgb
        .slice(1)
        .map((x) => Number(x).toString(16).padStart(2, '0'))
        .join('')
    );
  return null;
}
const channels = (hex) =>
  [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
function luminance(hex) {
  return channels(hex).reduce((total, value, i) => {
    const n = value / 255;
    return (
      total +
      [0.2126, 0.7152, 0.0722][i] *
        (n <= 0.04045 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4)
    );
  }, 0);
}
function contrast(a, b) {
  const x = luminance(a),
    y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}
function blend(a, b, amount) {
  const other = channels(b);
  return (
    '#' +
    channels(a)
      .map((v, i) =>
        Math.round(v + (other[i] - v) * amount)
          .toString(16)
          .padStart(2, '0')
      )
      .join('')
  );
}
function readable(foreground, background) {
  if (contrast(foreground, background) >= 4.5) return foreground;
  const target =
    contrast('#000000', background) >= contrast('#ffffff', background)
      ? '#000000'
      : '#ffffff';
  for (let step = 1; step <= 20; step++) {
    const adjusted = blend(foreground, target, step / 20);
    if (contrast(adjusted, background) >= 4.5) return adjusted;
  }
  return target;
}
function length(value, maxPx, maxRem, minPx = 0, minRem = 0) {
  if (value === '0' && minPx === 0) return '0';
  const match = value.match(/^(\d+(?:\.\d+)?|\.\d+)(px|rem)$/);
  if (!match) return null;
  const n = Number(match[1]);
  const [min, max] = match[2] === 'px' ? [minPx, maxPx] : [minRem, maxRem];
  return n >= min && n <= max ? `${n}${match[2]}` : null;
}
export const campaignBaseColors = Object.freeze({
  color: '#fff7ee',
  background: '#241e19'
});

export function campaignInlineStyle(
  raw,
  parent = campaignBaseColors,
  tag = 'span'
) {
  const styles = new Map();
  for (const declaration of String(raw || '')
    .slice(0, 1000)
    .split(';')
    .slice(0, 24)) {
    const separator = declaration.indexOf(':');
    if (separator < 0) continue;
    let property = declaration.slice(0, separator).trim().toLowerCase();
    const value = declaration
      .slice(separator + 1)
      .trim()
      .toLowerCase();
    let safe = null;
    if (property === 'background') property = 'background-color';
    if (property === 'color' || property === 'background-color')
      safe =
        property === 'background-color' && value === 'transparent'
          ? value
          : color(value);
    else if (
      property === 'text-align' &&
      /^(?:left|right|center|start|end)$/.test(value)
    )
      safe = value;
    else if (
      property === 'font-weight' &&
      /^(?:[4-9]00|normal|bold)$/.test(value)
    )
      safe = value;
    else if (property === 'font-size') safe = length(value, 24, 1.5, 14, 0.875);
    else if (property === 'padding' || property === 'border-radius') {
      const values = value.split(/\s+/);
      const parsed = values.map((v) =>
        property === 'padding' ? length(v, 16, 1) : length(v, 24, 1.5)
      );
      if (values.length <= 4 && parsed.every((x) => x !== null))
        safe = parsed.join(' ');
    } else if (
      property === 'line-height' &&
      /^(?:\d+(?:\.\d+)?|\.\d+)$/.test(value) &&
      Number(value) >= 1.2 &&
      Number(value) <= 2
    )
      safe = String(Number(value));
    else if (property === 'font-style' && /^(?:normal|italic)$/.test(value))
      safe = value;
    else if (property === 'text-decoration' && value === 'underline')
      safe = value;
    if (safe !== null) styles.set(property, safe);
  }
  // Mirror the header's existing link/code defaults. Opaque inline colors are
  // checked against the inherited background; CSS cannot create hidden text.
  const defaultColor = ['a', 'code'].includes(tag) ? '#ffe0ac' : parent.color;
  const defaultBackground =
    tag === 'code'
      ? blend(parent.background, '#ffffff', 18 / 255)
      : parent.background;
  const requestedBackground = styles.get('background-color');
  const background =
    requestedBackground === 'transparent'
      ? parent.background
      : requestedBackground || defaultBackground;
  const requestedColor = styles.get('color') || defaultColor;
  const foreground = readable(requestedColor, background);
  if (styles.has('color') || foreground !== requestedColor)
    styles.set('color', foreground);
  return {
    css: [...styles].map(([key, value]) => `${key}:${value}`).join(';'),
    context: { color: foreground, background }
  };
}
